import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { AgentUpdateSignup } from "@/components/agent-update-signup";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What are the new AML obligations for real estate agents in Australia?",
    a: "From 1 July 2026 real estate agencies are reporting entities under the Anti-Money Laundering and Counter-Terrorism Financing Act as part of the tranche 2 reforms. That means enrolling with AUSTRAC, having an AML/CTF program, appointing a compliance officer, carrying out customer due diligence on the parties to a designated service, and reporting suspicious matters.",
  },
  {
    q: "What is a reliance agreement?",
    a: "The AML/CTF regime lets one reporting entity rely on customer due diligence carried out by another reporting entity, under a written agreement, instead of duplicating the work. Because law practices are also reporting entities, an agency can enter a reliance arrangement with us: we carry out and record the identity verification and due diligence, and the agency relies on it rather than building the capability in-house.",
  },
  {
    q: "What does Coastside Law actually do under the arrangement?",
    a: "We verify the identity of the seller (and, where the agency's designated service requires it, the buyer), check beneficial ownership for companies and trusts, screen against politically exposed person and sanctions lists, record the outcome, and give the agency a compliant record it can produce to AUSTRAC. Records are kept for the statutory retention period and are available to the agency on request.",
  },
  {
    q: "Does reliance transfer our liability to you?",
    a: "No — and be wary of anyone who says it does. Under a reliance arrangement the agency remains the reporting entity responsible for its own obligations; what reliance does is let you use our due diligence rather than repeating it, provided the agreement, the checks and the record-keeping meet the requirements. We set the agreement up so those conditions are properly documented.",
  },
  {
    q: "How does it work day to day?",
    a: "The agent sends us the listing or contract details. We contact the party directly, run the verification electronically (usually the same day), and return a completed due diligence record to the office. In a conveyancing matter we are already verifying identity for the transaction, so for many agencies the marginal cost of the AML work is small.",
  },
  {
    q: "What does it cost?",
    a: `We quote a fixed per-party fee, and a lower per-matter rate for agencies that send us their conveyancing work as well. Call ${FIRM.phone} and we will put a proposal and a draft reliance agreement in front of you.`,
  },
];

const SERVICE = [
  "Written reliance agreement drafted for your agency",
  "Electronic identity verification of individuals",
  "Company, trust and beneficial ownership checks",
  "PEP and sanctions screening",
  "Source of funds enquiries where the risk rating requires them",
  "Compliant, retained records available to your office and to AUSTRAC",
  "Escalation and suspicious matter guidance from a solicitor",
  "Agent and staff briefing on what to collect at listing",
];

export const Route = createFileRoute("/aml-real-estate-agents")({
  head: () => ({
    meta: [
      { title: fitTitle(`AML Checks for Real Estate Agents | Reliance Agreements QLD | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Solicitor-run AML customer due diligence for Queensland real estate agencies under a written reliance agreement — identity verification, beneficial ownership, PEP screening and compliant records for tranche 2."),
      },
      { property: "og:title", content: fitTitle(`AML Reliance Agreements for Real Estate Agents | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Tranche 2 AML obligations start 1 July 2026. We run the customer due diligence for your agency under a reliance agreement."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/aml-real-estate-agents" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/aml-real-estate-agents" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: AmlPage,
});

function AmlPage() {
  return (
    <>
      <PageHero
        eyebrow="Services for real estate agencies"
        title="AML customer due diligence for real estate agents, under a reliance agreement"
        lead="Tranche 2 brings real estate agencies into the AML/CTF regime from 1 July 2026. Rather than building verification capability in-house, your agency can rely on customer due diligence carried out by our law practice under a written reliance agreement — and get a compliant record back the same day."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">What we provide</h2>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {SERVICE.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Why reliance rather than in-house</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Doing it in-house means training every sales agent to collect and verify identity documents,
          buying a verification platform, storing sensitive identity data securely for years, and having
          someone in the office who can tell the difference between an awkward client and a suspicious
          matter. For a small or mid-size agency that is a disproportionate amount of machinery for work
          that a law practice already does on every conveyance.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Under the arrangement your agency stays the reporting entity and keeps its own enrolment,
          program and compliance officer — reliance covers the customer due diligence limb, properly
          documented, so the checks are done once by people who do them daily.
        </p>

        <div className="mt-16 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">The other thing agents ask us for</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Most agencies that set up a reliance agreement also hand us their Form 2 seller disclosure
            packs, so the listing goes to market compliant on both fronts.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link to="/seller-disclosure" className="text-primary underline">
              Form 2 seller disclosure statements
            </Link>
            <Link to="/conveyancing" className="text-primary underline">
              Fixed-fee conveyancing for your buyers and sellers
            </Link>
            <Link
              to="/guides/$slug"
              params={{ slug: "aml-tranche-2-real-estate-agents-what-agencies-are-getting-wrong" }}
              className="text-primary underline"
            >
              What agencies are getting wrong since 1 July
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who your agency deals with" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Questions agency principals ask about AML
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <AgentUpdateSignup heading="Stay across it — the fortnightly agent & broker update" />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Ask about a reliance agreement for your agency" />
        </div>
      </section>

      <CtaBand
        title="Eight weeks into tranche 2 — how is your agency actually going?"
        body={`Call ${FIRM.phone} for a no-charge review of how AML is running in your office, and what a reliance agreement would cost.`}
      />
      <div className="h-20" />
    </>
  );
}
